import makeSqliteStorer from 'services/storage/local/makeSqliteStorer'
import arraySerializer from 'src/shared/serializers/arraySerializer'
import { computed, ref, watch } from 'vue'
import { cloneDeep, keyBy } from 'lodash-es'
import { Notify } from 'quasar'
import { mdiCloudUploadOutline } from '@quasar/extras/mdi-v7'
import { useAuthState } from 'services/auth/useAuthState'
import { defineStore } from 'pinia'
import { useNetworkStatus } from 'src/shared/composables/useNetworkStatus/useNetworkStatus'
import orionPiniaOrm from 'config/orion-pinia-orm/orion-pinia-orm'
import { getApiBaseUrl } from 'utils/getApiBaseUrl'
import { useRest } from '../rest/useRest'
import { offlineCreateReachedServer } from './offlineCreateReachedServer'
import {
  applyUploadedIds,
  buildOfflineQueueSnapshot,
  hydrateQueueFromSnapshot,
  resourceIdKey,
  shouldWriteOfflineQueueSnapshot,
  type QueueTask,
} from './offlineQueue'
import {
  readOfflineQueueSnapshot,
  writeOfflineQueueSnapshot,
} from './offlineQueueSnapshotStore'
import { clearOfflineQueueSnapshot } from './clearOfflineQueueSnapshot'
import { readFuelloxAuthToken } from 'src/auth/readFuelloxAuthToken'

export type { QueueTask } from './offlineQueue'

function wait (milliseconds: number) {
  return new Promise(resolve => {
    setTimeout(resolve, milliseconds)
  })
}

export interface QueueStatus {
  status: 'working' | 'stopped'
  task: QueueTask | null
  progress: number
}

export const useQueue = defineStore('queue', () => {
  const authState = useAuthState()
  const networkStatusService = useNetworkStatus()

  const queueStorerLegacy = makeSqliteStorer(
    'fuellox',
    'useQueue:storer',
  )
  const queueStorer = makeSqliteStorer(
    `${authState.tenantId.value}-${authState.orgId.value}`,
    'useQueue:storer',
  )
  const tokenStorer = makeSqliteStorer('fuellox', 'fuelloxToken')

  const queableModels = keyBy(orionPiniaOrm.queueables || [], 'entity')
  const queue = ref<QueueTask[]>([])
  const legacyQueue = ref<QueueTask[]>([])
  const sessionHadTasks = ref(false)

  const endpoint = ref('')

  const rest = useRest(endpoint)

  const status = ref<QueueStatus>({
    status: 'stopped',
    task: null,
    progress: 0,
  })

  function rememberSessionTasks () {
    if (queue.value.length > 0 || legacyQueue.value.length > 0) {
      sessionHadTasks.value = true
    }
  }

  async function restoreQueueFromSnapshot () {
    const snapshot = await readOfflineQueueSnapshot()
    const combined = [...queue.value, ...legacyQueue.value]
    const next = hydrateQueueFromSnapshot(combined, snapshot)
    if (combined.length === 0) {
      queue.value = next
    } else {
      const storedIds = new Set(combined.map(task => resourceIdKey(task.resourceId)))
      queue.value = [
        ...queue.value,
        ...next.filter(task => !storedIds.has(resourceIdKey(task.resourceId))),
      ]
    }
    rememberSessionTasks()
  }

  async function setQueueFromStorage () {
    const rawQueue = await queueStorer.get()
    const queueResult = rawQueue ? arraySerializer.read(rawQueue) : []
    queue.value = queueResult

    const rawQueueLegacy = await queueStorerLegacy.get()
    const legacyQueueResult = rawQueueLegacy ? arraySerializer.read(rawQueueLegacy) : []
    legacyQueue.value = legacyQueueResult
    await restoreQueueFromSnapshot()
  }

  const hydrated = setQueueFromStorage()

  async function persistOfflineQueueSnapshot () {
    const existing = await readOfflineQueueSnapshot()
    const tasks = [...queue.value, ...legacyQueue.value]
    const remaining = applyUploadedIds(
      existing?.tasks ?? [],
      existing?.uploadedResourceIds ?? [],
    )
    if (!shouldWriteOfflineQueueSnapshot({
      nextTaskCount: tasks.length,
      existingRemainingCount: remaining.length,
      sessionHadTasks: sessionHadTasks.value,
    })) {
      return
    }
    const token = (await readFuelloxAuthToken(() => tokenStorer.get())) || existing?.token || ''
    await writeOfflineQueueSnapshot(buildOfflineQueueSnapshot({
      apiEndpoint: `${getApiBaseUrl()}/api`,
      token,
      tenantId: authState.tenantId.value,
      orgId: authState.orgId.value,
      tasks,
      uploadedResourceIds: existing?.uploadedResourceIds ?? [],
    }))
  }

  async function syncWithStoredQueue () {
    await Promise.all([
      queueStorer.set(arraySerializer.write(queue.value)),
      queueStorerLegacy.set(arraySerializer.write(legacyQueue.value)),
    ])
    await persistOfflineQueueSnapshot()
  }

  async function applyHeadlessUploads () {
    const snapshot = await readOfflineQueueSnapshot()
    const uploadedResourceIds = snapshot?.uploadedResourceIds ?? []
    if (uploadedResourceIds.length === 0) {
      return
    }
    queue.value = applyUploadedIds(queue.value, uploadedResourceIds)
    legacyQueue.value = applyUploadedIds(legacyQueue.value, uploadedResourceIds)
  }

  async function enqueue (task: QueueTask) {
    await hydrated
    if (queableModels[task.entity]) {
      task.createdOn = new Date()
      queue.value.push(task)
      sessionHadTasks.value = true

      await syncWithStoredQueue()
      return
    }
    console.warn('cannot enqueue a task for an unknown entity', task.entity)
  }

  async function remove (task: QueueTask, isLegacy: boolean) {
    await hydrated
    const selectedQueue = isLegacy ? legacyQueue : queue

    const taskIndex = selectedQueue.value.findIndex(currentTask => {
      return currentTask.resourceId === task.resourceId
    })
    if (taskIndex !== -1) selectedQueue.value.splice(taskIndex, 1)

    await syncWithStoredQueue()
  }

  async function executeTask (task: QueueTask, isLegacy: boolean) {
    status.value.task = task
    if (!task.entity) {
      console.warn('cannot execute a task with no entity')
      return
    }

    if (task.action === 'create') {
      endpoint.value = task.entity
      const form = { ...task.data, id: task.resourceId }
      try {
        await rest.execute('post', form)
      } catch {
      }
    }

    if (offlineCreateReachedServer({
      hasErrors: rest.hasErrors.value,
      responseData: rest.data.value,
    })) {
      await remove(task, isLegacy)
    }
  }

  async function work (options?: { wait?: number, silent?: boolean }) {
    if (status.value.status === 'working') {
      return 'queue already working'
    }

    await hydrated
    await restoreQueueFromSnapshot()
    await applyHeadlessUploads()
    await syncWithStoredQueue()

    if (options?.wait) {
      await wait(5000)
    }
    status.value.status = 'working'

    try {
      const clonedQueue = cloneDeep(queue.value)
      const clonedQueueLegacy = cloneDeep(legacyQueue.value)

      const originalQueueLength = clonedQueue.length
      const originalQueueLengthLegacy = clonedQueueLegacy.length

      for (const task of clonedQueue) {
        if (status.value.status !== 'working') {
          break
        }
        await executeTask(task, false)
      }

      for (const task of clonedQueueLegacy) {
        if (status.value.status !== 'working') {
          break
        }
        await executeTask(task, true)
      }

      if (
        !options?.silent &&
        (
          (originalQueueLength && (queue.value.length <= 0)) ||
          (originalQueueLengthLegacy && (legacyQueue.value.length <= 0))
        )
      ) {
        Notify.create({
          color: 'positive',
          timeout: 8000,
          message: 'Device data successfully synced',
          icon: mdiCloudUploadOutline,
        })
      }
    } catch (err) {
      console.error(err)
      return false
    } finally {
      status.value.status = 'stopped'
    }

    return true
  }

  function stop () {
    status.value.status = 'stopped'
  }

  async function flush () {
    await hydrated
    queue.value = []
    await syncWithStoredQueue()
  }

  async function flushLegacy () {
    await hydrated
    legacyQueue.value = []
    await syncWithStoredQueue()
  }

  async function discardForIdentityReplacement (): Promise<void> {
    stop()
    await flush()
    await flushLegacy()
    await clearOfflineQueueSnapshot()
  }

  const allQueue = computed(() => [...(queue.value ?? []), ...(legacyQueue.value ?? [])])

  watch(
    networkStatusService.connected,
    (isConnected) => {
      if (isConnected) work({ wait: 5000 })
    },
    { immediate: true },
  )

  return {
    status,
    syncWithStoredQueue,
    enqueue,
    remove,
    executeTask,
    work,
    stop,
    queue,
    legacyQueue,
    allQueue,
    flush,
    flushLegacy,
    discardForIdentityReplacement,
  }
})
